/**
 * Unified hook for fetching portfolio snapshots in visualization widgets.
 * Works in both advisor and public flows — the surface and session token come
 * from `usePublicSession()`.
 *
 * Uses separate query keys + API calls for advisor vs public, but only enables
 * the appropriate one. This avoids conditional hook calls while keeping widget code clean.
 */

import type { GoalSnapshotResponse } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import * as advisorApi from "@/lib/api/flow-instances/portfolio-snapshots";
import * as publicApi from "@/lib/api/public-flow/portfolio-snapshots";
import { usePublicSession } from "@/lib/context/public-session-context";

type SnapshotType =
  | "portfolio"
  | "xray"
  | "assetAllocation"
  | "historicalReturn"
  | "forecast"
  | "cost"
  | "expectedValue"
  // QL-416 — cost/benefit comparison (existing side embedded).
  | "costBenefit";

type SnapshotHookResult = {
  data: unknown | null;
  generatedAt: string | null;
  isLoading: boolean;
};

const ADVISOR_FN: Record<SnapshotType, (portfolioId: string) => Promise<GoalSnapshotResponse>> = {
  portfolio: advisorApi.getPortfolioPortfolioSnapshot,
  xray: advisorApi.getPortfolioXraySnapshot,
  assetAllocation: advisorApi.getPortfolioAssetAllocationSnapshot,
  historicalReturn: advisorApi.getPortfolioHistoricalReturnSnapshot,
  forecast: advisorApi.getPortfolioForecastSnapshot,
  cost: advisorApi.getPortfolioCostSnapshot,
  expectedValue: advisorApi.getPortfolioExpectedValueSnapshot,
  costBenefit: advisorApi.getPortfolioCostBenefitSnapshot,
};

const PUBLIC_FN: Record<
  SnapshotType,
  (token: string, portfolioId: string) => Promise<GoalSnapshotResponse>
> = {
  portfolio: publicApi.getPublicPortfolioPortfolioSnapshot,
  xray: publicApi.getPublicPortfolioXraySnapshot,
  assetAllocation: publicApi.getPublicPortfolioAssetAllocationSnapshot,
  historicalReturn: publicApi.getPublicPortfolioHistoricalReturnSnapshot,
  forecast: publicApi.getPublicPortfolioForecastSnapshot,
  cost: publicApi.getPublicPortfolioCostSnapshot,
  expectedValue: publicApi.getPublicPortfolioExpectedValueSnapshot,
  costBenefit: publicApi.getPublicPortfolioCostBenefitSnapshot,
};

/**
 * Fetch a portfolio snapshot for visualization widgets.
 *
 * @param snapshotType - Which snapshot type to fetch
 * @param portfolioId - The portfolio ID (null disables the query)
 */
export function usePortfolioSnapshot(
  snapshotType: SnapshotType,
  portfolioId: string | null,
): SnapshotHookResult {
  const session = usePublicSession();
  const isPublic = session.isPublic;
  const prefix = isPublic ? "publicFlow" : "flowInstances";
  const queryKey = [prefix, "portfolios", portfolioId, "snapshot", snapshotType] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => {
      if (session.isPublic) {
        return PUBLIC_FN[snapshotType](session.sessionToken, portfolioId!);
      }
      return ADVISOR_FN[snapshotType](portfolioId!);
    },
    enabled: !!portfolioId,
  });

  return {
    data: data?.data ?? null,
    generatedAt: data?.generatedAt ?? null,
    isLoading,
  };
}
